import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabase';

type Props = {
  onSignOut: () => void;
};

const AccountPrivacy: React.FC<Props> = ({ onSignOut }) => {
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const remove = async () => {
    if (typed.trim().toUpperCase() !== 'DELETE' || working) return;
    setWorking(true);
    setError('');
    const { data, error: rpcError } = await supabase.rpc('delete_own_account');
    if (rpcError || data?.success !== true) {
      setError('Could not delete the account. Try again or use the contact form.');
      setWorking(false);
      return;
    }
    await supabase.auth.signOut();
    onSignOut();
  };

  return (
    <div className="card p-6 border border-gray-200 bg-white">
      <h3 className="text-xl font-bold text-gray-900 mb-2">Account, billing, and privacy</h3>
      <p className="text-sm text-gray-600 mb-4">
        ClubIQ has no paid plan today, so there is no subscription to cancel and no auto-renewal.
        When a paid plan exists, cancel will live on this same page, in one step, and we will send
        a renewal reminder before each charge.
      </p>
      <p className="text-sm text-gray-600 mb-4">
        <Link to="/privacy" className="text-green-700 font-medium underline">Privacy Policy</Link>
        {' · '}
        <Link to="/terms" className="text-green-700 font-medium underline">Terms of Service</Link>
      </p>
      {!confirm ? (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          className="px-4 py-2 rounded-lg border border-red-300 text-red-700 font-semibold hover:bg-red-50"
        >
          Delete my account
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This permanently deletes your login, golf profile, bag, distances, and first-party
            events. Type DELETE to confirm.
          </p>
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="DELETE"
            autoComplete="off"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={typed.trim().toUpperCase() !== 'DELETE' || working}
              onClick={remove}
              className="px-4 py-2 rounded-lg bg-red-700 text-white font-semibold disabled:opacity-50"
            >
              {working ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirm(false);
                setTyped('');
                setError('');
              }}
              className="px-4 py-2 rounded-lg border"
            >
              Keep account
            </button>
          </div>
          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default AccountPrivacy;
